/** DC fast connector types on EV UA stations. */
export const EVUA_FAST_CONNECTORS = new Set(['ccs1', 'ccs2', 'chd', 'gbt', 'nacs', 'gbtadapter']);

/** AC connector types on EV UA stations (Slow chip). */
export const EVUA_AC_CONNECTORS = new Set(['type1', 'type2', 'type2socket', 'gbtac']);

/** Slow chip: mixed AC+DC hubs (Garbuz, YASNO + Type 2 socket) show as one AC pin at this kW. */
export const EVUA_MIXED_HUB_AC_POWER_KW = 7;

/** Slow chip: keep AC below this kW. 50 kW DC rows must not appear as Slow. */
export const MAP_FILTER_SLOW_MAX_KW = 30;

/** Homepage chip: keep EV UA + 220-km stations at this rated power or above. */
export const MAP_FILTER_160_KW = 160;

/** Rated kW from watts or EV UA maxpow/minpow; 0 when unknown (do not treat as 160+). */
export function getRatedPowerKw(port) {
  const watts = Number(port?.maxPowerWt ?? port?.max_power_wt);
  if (Number.isFinite(watts) && watts > 0) {
    return watts / 1000;
  }
  const apiKw = Number(port?.maxpow ?? port?.minpow);
  if (Number.isFinite(apiKw) && apiKw > 0) {
    return apiKw;
  }
  return 0;
}

export function hasMinRatedPowerKw(port, minKw) {
  if (!minKw) return true;
  return getRatedPowerKw(port) >= minKw;
}

export function powerMeetsMinKw(powerKw, minKw) {
  if (!minKw) return true;
  return (Number(powerKw) || 0) >= minKw;
}

/** Hide an EV UA pin when its 220-km twin is also drawn (and passes the min-kW chip). */
export function shouldOmitMatchedEvuaPin(match, { omitMatched = false, minPowerKw = 0 } = {}) {
  if (!omitMatched || !match) return false;
  return powerMeetsMinKw(match.powerKw, minPowerKw);
}

/** 160+ under + Roaming is the high-power map mode. */
export function isHighPowerMapMode(filter80Kw, filter160Kw = false) {
  return Boolean(filter80Kw && filter160Kw);
}

export function hasEvua80KwConnector(port) {
  const connectors = port?.con;
  if (!Array.isArray(connectors)) return false;
  return connectors.some(c => EVUA_FAST_CONNECTORS.has(c));
}

function connectorsOf(port) {
  return Array.isArray(port?.con) ? port.con : [];
}

export function hasEvuaNacsConnector(port) {
  return connectorsOf(port).includes('nacs');
}

function hasNonNacsDcConnector(port) {
  return connectorsOf(port).some(c => c === 'ccs1' || c === 'ccs2' || c === 'chd' || c === 'gbt' || c === 'gbtadapter');
}

/** DC Fast chip: CCS/CHAdeMO/GB/T, or NACS at 30 kW+. Low-power NACS is AC. */
export function hasEvuaDcConnector(port) {
  if (hasNonNacsDcConnector(port)) return true;
  if (!connectorsOf(port).includes('nacs')) return false;
  const kw = getRatedPowerKw(port);
  return kw >= 30 || kw === 0;
}

/** Slow chip: Type 1/2, GB/T AC, or NACS-only below 30 kW. */
export function hasEvuaAcConnector(port) {
  const connectors = connectorsOf(port);
  if (connectors.some(c => EVUA_AC_CONNECTORS.has(c))) return true;
  if (!connectors.includes('nacs') || hasNonNacsDcConnector(port)) return false;
  const kw = getRatedPowerKw(port);
  return kw > 0 && kw < 30;
}

export function evuaPortMatchesAcdc(port, acdc = 'all') {
  if (acdc === 'dc') return hasEvuaDcConnector(port);
  if (acdc === 'ac') return hasEvuaAcConnector(port);
  return true;
}

export function evuaAcdcForMapFilters({ filter160Kw = false, portFilter = 'all' } = {}) {
  if (filter160Kw) return 'dc';
  if (portFilter === 'fast') return 'dc';
  if (portFilter === 'slow') return 'ac';
  return 'all';
}

function ratedPowerIsDcSized(port) {
  return getRatedPowerKw(port) >= MAP_FILTER_SLOW_MAX_KW;
}

/** Mixed AC+DC hub, or AC connector whose listed kW is DC-sized (YASNO 50 kW + Type 2 socket). */
export function isEvuaSlowMixedHub(port) {
  const hasAc = hasEvuaAcConnector(port);
  const hasDc = hasEvuaDcConnector(port);
  if (hasAc && hasDc) return true;
  if (hasAc && ratedPowerIsDcSized(port)) return true;
  const minp = Number(port?.minp);
  const maxp = Number(port?.maxp);
  const hasRange = Number.isFinite(minp) && minp > 0 && Number.isFinite(maxp) && maxp > 0 && minp !== maxp;
  return Boolean(hasDc && hasRange);
}

/** Slow kW: minpow when it is AC-sized, otherwise 7 kW for mixed hubs. Null when not Slow. */
export function getEvuaSlowPowerKw(port) {
  const minpow = Number(port?.minpow);
  const maxpow = Number(port?.maxpow);
  const minOk = Number.isFinite(minpow) && minpow > 0 && minpow < MAP_FILTER_SLOW_MAX_KW;
  const maxOk = Number.isFinite(maxpow) && maxpow > 0 && maxpow < MAP_FILTER_SLOW_MAX_KW;
  if (minOk) return minpow;
  if (maxOk) return maxpow;
  if (hasEvuaAcConnector(port) || isEvuaSlowMixedHub(port)) return EVUA_MIXED_HUB_AC_POWER_KW;
  return null;
}

/** Merge jittered spreadsheet siblings (~36 m) and the rest of a roadside hub. */
const MIXED_HUB_MERGE_METERS = 180;

function stationDistanceMeters(a, b) {
  const lat1 = Number(a?.lat ?? a?.latitude);
  const lon1 = Number(a?.lon ?? a?.longitude);
  const lat2 = Number(b?.lat ?? b?.latitude);
  const lon2 = Number(b?.lon ?? b?.longitude);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  const dy = (lat1 - lat2) * 111000;
  const dx = (lon1 - lon2) * 111000 * Math.cos((lat1 * Math.PI) / 180);
  return Math.hypot(dx, dy);
}

function toSlowAcHubPort(members) {
  const first = [...members].sort((a, b) => Number(a?.key) - Number(b?.key))[0];
  const acCons = [];
  const seen = new Set();
  for (const port of members) {
    for (const code of connectorsOf(port)) {
      if (EVUA_AC_CONNECTORS.has(code) && !seen.has(code)) {
        seen.add(code);
        acCons.push(code);
      }
    }
  }
  if (acCons.length === 0) {
    acCons.push('type1', 'type2');
    if (members.some(port => connectorsOf(port).includes('gbt') || connectorsOf(port).includes('gbtac'))) {
      acCons.push('gbtac');
    }
  }
  const prices = members.map(port => Number(port?.minp)).filter(n => Number.isFinite(n) && n > 0);
  const minp = prices.length ? Math.min(...prices) : Number(first?.minp);
  const acKw = members
    .map(port => Number(port?.minpow))
    .filter(n => Number.isFinite(n) && n > 0 && n < MAP_FILTER_SLOW_MAX_KW);
  const slowKw = acKw.length ? Math.min(...acKw) : EVUA_MIXED_HUB_AC_POWER_KW;
  return {
    ...first,
    con: acCons,
    minpow: slowKw,
    maxpow: slowKw,
    minp: Number.isFinite(minp) ? minp : first?.minp,
    maxp: Number.isFinite(minp) ? minp : first?.minp,
  };
}

/**
 * Slow: mixed AC+DC hubs become one 7 kW AC pin (minp). Fast still sees the DC siblings.
 * Nearby AC-only rows (Go To-U at Garbuz) join that pin; GPS jitter does not split the hub.
 */
export function collapseMixedEvuaHubsForSlow(ports) {
  if (!Array.isArray(ports) || ports.length === 0) return [];
  const mixed = [];
  const passthrough = [];
  for (const port of ports) {
    if (isEvuaSlowMixedHub(port)) mixed.push(port);
    else passthrough.push(port);
  }
  const parent = mixed.map((_, index) => index);
  const find = index => (parent[index] === index ? index : (parent[index] = find(parent[index])));
  const union = (i, j) => {
    const a = find(i);
    const b = find(j);
    if (a !== b) parent[a] = b;
  };
  for (let i = 0; i < mixed.length; i++) {
    for (let j = i + 1; j < mixed.length; j++) {
      if (stationDistanceMeters(mixed[i], mixed[j]) <= MIXED_HUB_MERGE_METERS) union(i, j);
    }
  }
  const groups = new globalThis.Map();
  mixed.forEach((port, index) => {
    const root = find(index);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(port);
  });
  const absorbed = new Set();
  for (const members of groups.values()) {
    for (const port of passthrough) {
      if (absorbed.has(port)) continue;
      if (members.some(member => stationDistanceMeters(member, port) <= MIXED_HUB_MERGE_METERS)) {
        members.push(port);
        absorbed.add(port);
      }
    }
  }
  const remaining = passthrough.filter(port => !absorbed.has(port));
  return [...remaining, ...[...groups.values()].map(toSlowAcHubPort)];
}

function evuaPortMatchesChina(port, acdc = 'all') {
  const connectors = connectorsOf(port);
  if (acdc === 'ac') return connectors.includes('gbtac');
  if (acdc === 'dc') return connectors.includes('gbt') || connectors.includes('gbtadapter');
  return connectors.includes('gbt') || connectors.includes('gbtac') || connectors.includes('gbtadapter');
}

/**
 * EVUA {@code minp}/{@code maxp} are price in UAH/kWh (not kW).
 * When both exist, {@code minp} is AC (type1/type2) and {@code maxp} is DC.
 * Fast must ignore {@code minp} so hubs like Garbuz (AC 16.99, DC 21.99) stay out of cheapest.
 */
export function getEvuaPriceUahPerKwh(port, { acdc = 'all' } = {}) {
  const minp = Number(port?.minp);
  const maxp = Number(port?.maxp);
  const minOk = Number.isFinite(minp) && minp > 0;
  const maxOk = Number.isFinite(maxp) && maxp > 0;

  if (acdc === 'dc') {
    if (maxOk) return maxp;
    if (minOk && !hasEvuaAcConnector(port)) return minp;
    return null;
  }
  if (acdc === 'ac') {
    if (minOk) return minp;
    return null;
  }
  if (minOk) return minp;
  if (maxOk) return maxp;
  return null;
}

export function isEvuaPortCheaperThanDamPlusUah(port, damRetailUahPerKwh, extraUah = 7, acdc = 'all') {
  const price = getEvuaPriceUahPerKwh(port, { acdc });
  if (price == null || damRetailUahPerKwh == null) return false;
  return price < damRetailUahPerKwh + extraUah;
}

/** Share of cheapest EVUA stations when + Roaming + Cheapest (by minp/maxp only, not Open EMS). */
export const EVUA_LOWEST_PRICE_PERCENT = 20;

/** + Roaming + 160+ + Cheapest: keep the cheapest third of 160 kW+ stations. */
export const CHEAPEST_160_SHARE = 1 / 3;

/** + Roaming + Cheapest (without 160+): EVUA-only map, bottom {@link EVUA_LOWEST_PRICE_PERCENT}% by price. */
export function isEvuaExclusiveMapMode(filter80Kw, cheapestFilter, filter160Kw = false) {
  return Boolean(filter80Kw && cheapestFilter && !filter160Kw);
}

/**
 * Keeps the cheapest {@code share} of ports that have a price (ties at the cutoff stay in).
 * @param {object[]} ports
 * @param {(port: object) => number|null} getPrice
 * @param {number} share fraction 0..1 (e.g. 1/3)
 */
export function filterLowestPriceShare(ports, getPrice, share = CHEAPEST_160_SHARE) {
  if (!Array.isArray(ports) || share <= 0 || typeof getPrice !== 'function') return [];

  const candidates = ports.filter(port => getPrice(port) != null);
  if (candidates.length === 0) return [];

  const sorted = [...candidates].sort((a, b) => getPrice(a) - getPrice(b));
  const takeCount = Math.max(1, Math.ceil(sorted.length * share));
  const cutoffPrice = getPrice(sorted[takeCount - 1]);

  return candidates.filter(port => getPrice(port) <= cutoffPrice);
}

/**
 * Keeps the cheapest {@code lowestPercent}% of EVUA stations that have minp/maxp (UAH/kWh).
 */
export function filterEvuaLowestPricePercent(
  ports,
  lowestPercent = EVUA_LOWEST_PRICE_PERCENT,
  carFilter = 'all',
  filter80Kw = false,
  acdc = 'all'
) {
  if (!Array.isArray(ports) || lowestPercent <= 0) return [];

  const getPrice = port => getEvuaPriceUahPerKwh(port, { acdc });
  let candidates = ports.filter(port => getPrice(port) != null);
  if (filter80Kw && carFilter === 'china') {
    candidates = candidates.filter(port => port?.con?.includes('gbt'));
  }
  if (candidates.length === 0) return [];

  const sorted = [...candidates].sort((a, b) => getPrice(a) - getPrice(b));
  const takeCount = Math.max(1, Math.ceil((sorted.length * lowestPercent) / 100));
  const cutoffPrice = getPrice(sorted[takeCount - 1]);

  return candidates.filter(port => getPrice(port) <= cutoffPrice);
}

/**
 * @param {object[]} ports
 * @param {{ filter80Kw?: boolean, filter160Kw?: boolean, cheapestFilter?: boolean, carFilter?: string, portFilter?: string, nacsFilter?: boolean, damRetailUahPerKwh?: number|null, extraUah?: number }} opts
 */
export function filterEvuaPortsForMap(ports, opts) {
  const {
    filter80Kw = false,
    filter160Kw = false,
    cheapestFilter = false,
    carFilter = 'all',
    portFilter = 'all',
    nacsFilter = false,
    damRetailUahPerKwh = null,
    extraUah = 7,
  } = opts ?? {};

  if (!Array.isArray(ports)) return [];

  const acdc = evuaAcdcForMapFilters({ filter160Kw, portFilter });
  const getPrice = port => getEvuaPriceUahPerKwh(port, { acdc });
  let source = ports;
  if (nacsFilter) {
    source = source.filter(hasEvuaNacsConnector);
  }
  let next = acdc === 'ac' ? collapseMixedEvuaHubsForSlow(source) : source;
  next = next.filter(port => evuaPortMatchesAcdc(port, acdc));
  if (acdc === 'ac') {
    next = next.filter(port => {
      const kw = getEvuaSlowPowerKw(port);
      return kw != null && kw < MAP_FILTER_SLOW_MAX_KW;
    });
  }
  if (carFilter === 'china') {
    next = next.filter(port => evuaPortMatchesChina(port, acdc));
  }

  if (filter160Kw && cheapestFilter) {
    next = next.filter(port => hasMinRatedPowerKw(port, MAP_FILTER_160_KW));
    return filterLowestPriceShare(next, getPrice, CHEAPEST_160_SHARE);
  }

  if (isEvuaExclusiveMapMode(filter80Kw, cheapestFilter, filter160Kw)) {
    return filterEvuaLowestPricePercent(next, EVUA_LOWEST_PRICE_PERCENT, 'all', false, acdc);
  }

  if (cheapestFilter) {
    next = next.filter(port => isEvuaPortCheaperThanDamPlusUah(port, damRetailUahPerKwh, extraUah, acdc));
  }
  return next;
}
